/**
 * Layer manager
 * @module
 */
var _ = require('optimizely-client-fn');

var actionTypes = require('../constants/action_types');
var DateEnv = require('../env/date');
var di = require('../di');
var Flux = require('../flux');

var GlobalStore = di.get('stores/global');
var SessionStore = di.get('stores/session');

var DEFAULT_LAYER_ACTIVATION_TIMEOUT = 2000;

/**
 * Records the layer's Decision after CDE runs.
 * Returns Decision for easy use in promise chaining
 * @param {string} layerId
 * @param {DecisionTicket} decisionTicket
 * @param {Decision} decision
 * @return {Decision}
 */
exports.recordLayerDecision = function(layerId:string, decisionTicket:DecisionTicket, decision:Decision): Decision {
  // TODO invariant that both args are set
  Flux.dispatch(actionTypes.RECORD_LAYER_DECISION, {
    layerId: layerId,
    decision: decision,
    decisionTicket: decisionTicket,
    sessionId: SessionStore.getSessionId(),
    activationId: GlobalStore.getActivationId(),
    timestamp: DateEnv.now(),
    revision: GlobalStore.getRevision(),
    namespace: GlobalStore.getNamespace(),
    pageId: decisionTicket.pageId,
  });
  return decision;
};

/**
 * Get unique list of related audience IDs for a layer
 *
 * @param {Layer} layer
 * @returns {string[]} list of related audience IDs
 */
exports.relatedAudienceIds = function(layer:Layer): string[] {
  var res = {} as any;
  var operators = ['and', 'or', 'not'];
  _.each(layer.experiments, function(exp) {
    _.each(_.flattenDeep(exp.audienceIds), function(audienceId) {
      if (!_.includes(operators, audienceId)) {
        res[audienceId] = true;
      }
    });
  });
  return _.keys(res);
};

/**
 * Get configured timeout for layer, falling back to default value
 *
 * @param {Layer} layer
 * @returns {number}
 */
exports.getActivationTimeout = function(layer:Layer): number {
  var activationSettings = layer.activation;
  if (activationSettings &&
      activationSettings.timeout !== null &&
      activationSettings.timeout !== undefined) {
    return activationSettings.timeout;
  }
  return DEFAULT_LAYER_ACTIVATION_TIMEOUT;
};

/**
 * Get descriptive string for a layer (for logging)
 * @param {Layer} layer
 * @returns {string}
 */
exports.description = function(layer:Layer): string {
  return (layer.name ? ('"' + layer.name + '" ') : '') + '(' + layer.id + ')';
};

/**
 * Construct a "single_experiment" Layer with the given `id`, `experimentId`, and a single `variationId`
 * but _don't_ load it into the LayerData store.
 * The experiment or variation IDs can be null when we're creating a layer to track a decision where
 * the visitor was excluded from the experiment for one reason or another.
 *
 * @param  {string} id
 * @param  {string} [experimentId]
 * @param  {string} [variationId]
 * @returns {Layer}
 */
exports.createSingle = function(id:string, experimentId?:string, variationId?:string): Layer {
  var layer:Layer = {
    id: id,
    policy: 'single_experiment',
    holdback: 0,
    experiments: [
      {
        id: experimentId || '',
        variations: [
          {
            id: variationId || '',
            actions: [],
          }
        ],
        audienceIds: []
      }
    ],
    integrationStringVersion: 1,
  };

  if (__INVARIANT__) {
    require('../utils/invariant').isType('Layer', layer);
  }

  return layer;
};

/**
 * Returns a layer state built with the given input.
 * The experiment or variation IDs can be null when we're creating a layer to track a decision where
 * the visitor was excluded from the experiment for one reason or another.
 *
 * @param  {string} layerId
 * @param  {string} [experimentId]
 * @param  {string} [variationId]
 * @param  {boolean} isLayerHoldback
 * @returns {LayerState}
 */
exports.createLayerState = function(layerId:string, experimentId?:string, variationId?:string, isLayerHoldback:boolean = false,
                                    isLayerConcluded:boolean = false): LayerState {
  var layerState:LayerState = {
    layerId: layerId,
    decision: {
      layerId: layerId,
      experimentId: experimentId,
      variationId: variationId,
      isLayerHoldback: isLayerHoldback || false,
      isLayerConcluded: isLayerConcluded || false,
    },
    decisionTicket: {
      audienceIds: [],
    },
  };

  if (__INVARIANT__) {
    require('../utils/invariant').isType('LayerState', layerState);
  }

  return layerState;
};

/**
 * @param {Layer} layer
 * @returns {Array.<string>} Unique list of integration types
 */
exports.getIntegrationTypes = function(layer:Layer): string[] {
  return _.keys(_.reduce(_.keys(layer.integrationSettings), function(res, settingKey) {
    if (!_.isNaN(Number(settingKey))) {
      // custom analytics are numeric IDs; coalesce to a single value
      settingKey = 'custom';
    }
    res[settingKey] = 1;
    return res;
  }, {}));
};
